// src/contexts/utils.tsx
// v2 — Ajout de resetRegieContext : nettoyage complet du contexte régie
//       appelé à l'arrêt ET au démarrage d'une chaîne.

import React from 'react';
import { BorrowHandle } from '../dataTypes/liveStreamManagment/sourceRegistry/types';
import { clearSnapshot } from '../hooks/useLiveStreamSnapshot';

// ── Nettoyage localStorage de la régie ───────────────────────────────────────
//
// Déclenché depuis BroadcastingChannelSheet quand la chaîne passe de
// active=true à active=false et que la sauvegarde API a réussi.
//
// Pour conserver une clé entre deux sessions, mettre sa ligne en commentaire.

export const MEDIARTC_LOCALSTORAGE_KEYS: string[] = [
  'mediartc_settings',              // Config serveur / bitrate / résolution canvas
  'mediartc_image_slots',           // Slots images overlay
  'mediartc_sound_slots',           // Slots sons
  'mediartc_pip_slots',             // Slots PiP vidéo
  'mediartc_jingle_slots',          // Slots jingles vidéo
  'mediartc_layout_slots',          // Scènes / layouts
  'mediartc_deck_slots_config',     // Config slots du deck
  'mediartc_negotiated_whip_codec', // Codec WHIP négocié
  'mediartc_screen_share_config',   // Configuration partage d'écran
  'mediartc_deck_keymap',           // Raccourcis clavier du deck
  'mediartc_external_sources',      // Sources URL externes
  'mediartc_slot_filenames',        // Noms de fichiers des slots
  'mediartc_source_aliases',        // Alias des sources
  'mediartc_source_audio_configs',  // Configs audio par source
];

export const clearMediartcLocalStorage = (): void => {
  MEDIARTC_LOCALSTORAGE_KEYS.forEach(key => {
    try { localStorage.removeItem(key); } catch {}
  });
  console.log('[régie] localStorage nettoyé (chaîne désactivée)');
};

// ── Types pour resetRegieContext ──────────────────────────────────────────────

export interface RegieResetRefs {
  videoRef:               React.RefObject<HTMLVideoElement>;
  canvasPreviewRef:       React.MutableRefObject<HTMLVideoElement | null>;
  deckPreviewVideoRef:    React.RefObject<HTMLVideoElement>;
  previewStreamsRef:       React.MutableRefObject<Map<string, MediaStream>>;
  activeSourceRef:        React.MutableRefObject<string | null>;
  currentBorrowHandleRef: React.MutableRefObject<BorrowHandle | null>;
  audioContextRef:        React.MutableRefObject<AudioContext | null>;
  /**
   * Filet de sécurité : cette connexion vit dans MediaStreamContext, donc PARTAGÉE
   * par toutes les chaînes (contrairement à tout le reste ici, qui est propre à
   * cette régie). Le nettoyage normal se fait à la FERMETURE de la chaîne
   * quittée (cf. le `return` de l'effet de montage dans LiveStreamManager, qui
   * appelle stopStreamingImpl) — ce reset au montage ne fait que rattraper le cas
   * où cette fermeture n'aurait pas eu lieu, pour ne jamais démarrer une chaîne
   * avec la connexion WHIP encore active d'une autre.
   */
  peerConnectionRef:      React.MutableRefObject<RTCPeerConnection | null>;
}

export interface RegieResetSetters {
  setSelectedVideoSource:   (v: string) => void;
  setCurrentStream:         (v: MediaStream | null) => void;
  setDeckPreviewSourceId:   (v: string | null) => void;
  setPreviewStreams:         (v: Map<string, MediaStream>) => void;
  // ── États d'activation ────────────────────────────────────────────────────
  setActiveImageIds:        (v: Set<string>) => void;
  setActiveSoundIds:        (v: Set<string>) => void;
  setActiveVideoOverlayIds: (v: Set<string>) => void;
  setActiveJingleId:        (v: string | null) => void;
  setActiveLayoutId:        (v: string | null) => void;
  setActiveLayoutPreviewId: (v: string | null) => void;
  setActiveSceneLayoutIds:  (v: string[]) => void;
  // ── Contenu des slots (fichiers chargés) ──────────────────────────────────
  // images/sons/PiP/jingles/partages d'écran n'y sont PLUS : depuis leur
  // cloisonnement par chaîne (MediaStreamContext), ce sont une config
  // persistante rechargée automatiquement par chaîne, pas un état de session
  // à remettre à zéro ici — sans quoi le mediaboard d'une chaîne se vidait à
  // chaque montage/changement de chaîne. Les scènes suivent la même règle.
  // ── État stream / enregistrement ──────────────────────────────────────────
  setIsStreamButtonActive:  (v: boolean) => void;
  setIsRecording:           (v: boolean) => void;
  setRecordingSessionId:    (v: number | null) => void;
  setRecordingStartedAt:    (v: number | null) => void;
  setPoweredOffSlots:       (v: Set<string>) => void;
  /** Cf. RegieResetRefs.peerConnectionRef — filet de sécurité, pas le chemin normal. */
  setIsStreaming:           (v: boolean) => void;
  // Switch audio (ControlBar) — optionnel pour ne pas casser les appelants qui
  // ne le gèrent pas. Type décrit en structurel : contexts/ ne doit pas dépendre
  // d'un composant. Cf. AudioSwitchState dans ControlBar.tsx.
  setAudioSwitchState?:     (v: { mode: 'source' | 'fixe' | 'mix'; fixedAudioSourceId: string; mixGains?: Record<string, number>; mixMuted?: Record<string, boolean> }) => void;
}

export interface RegieResetOptions {
  stopCompositingLoop:  () => void;
  scenePipelineManager: { deactivateScene: (id: string) => void };
  activeSceneLayoutIds: string[];
  registry:             { forceRelease: (id: string) => void };
  hlsProber:            { forceStatus: (id: string, s: 'failed' | 'ok' | 'probing') => void };
  hlsIds:               string[];
  /** Si true, efface aussi le snapshot localStorage de session */
  clearLocalStorage?:   boolean;
}

// ── resetRegieContext ─────────────────────────────────────────────────────────
//
// Nettoyage complet du contexte régie :
//   - Vide tous les éléments <video> (srcObject = null + pause)
//   - Arrête la boucle de compositing canvas
//   - Libère le borrow handle du flux final
//   - Ferme l'AudioContext
//   - Vide la Map des preview streams
//   - Désactive toutes les scènes actives via scenePipelineManager
//   - Force le statut 'failed' + libère les sources HLS dans le registry
//   - Réinitialise tous les états React liés à la session
//   - Efface optionnellement le snapshot localStorage

export function resetRegieContext(
  refs:    RegieResetRefs,
  setters: RegieResetSetters,
  options: RegieResetOptions,
): void {

  // ── 1. Éléments <video> ───────────────────────────────────────────────────
  const videoEls: (HTMLVideoElement | null)[] = [
    refs.videoRef.current,
    refs.canvasPreviewRef.current,
    refs.deckPreviewVideoRef.current,
  ];
  videoEls.forEach(el => {
    if (!el) return;
    el.pause();
    el.srcObject = null;
  });

  // ── 2. Boucle de compositing canvas ──────────────────────────────────────
  options.stopCompositingLoop();

  // ── 3. Borrow handle du flux final ────────────────────────────────────────
  if (refs.currentBorrowHandleRef.current) {
    try { refs.currentBorrowHandleRef.current.release(); } catch {}
    refs.currentBorrowHandleRef.current = null;
  }

  // ── 4. AudioContext ───────────────────────────────────────────────────────
  if (refs.audioContextRef.current) {
    try { refs.audioContextRef.current.close(); } catch {}
    refs.audioContextRef.current = null;
  }

  // ── 4bis. Connexion WHIP — voir le commentaire de RegieResetRefs.peerConnectionRef ──
  if (refs.peerConnectionRef.current) {
    try { refs.peerConnectionRef.current.close(); } catch {}
    refs.peerConnectionRef.current = null;
  }

  // ── 5. Preview streams ────────────────────────────────────────────────────
  refs.previewStreamsRef.current.clear();
  refs.activeSourceRef.current = null;

  // ── 6. Scènes actives ─────────────────────────────────────────────────────
  options.activeSceneLayoutIds.forEach(id => {
    try { options.scenePipelineManager.deactivateScene(id); } catch {}
  });

  // ── 7. Sources HLS dans le registry ──────────────────────────────────────
  options.hlsIds.forEach(id => {
    try { options.hlsProber.forceStatus(id, 'failed'); } catch {}
    try { options.registry.forceRelease(id); } catch {}
  });

  // ── 8. États React ────────────────────────────────────────────────────────

  // États d'activation (ce qui est "allumé")
  setters.setSelectedVideoSource('');
  setters.setCurrentStream(null);
  setters.setDeckPreviewSourceId(null);
  setters.setPreviewStreams(new Map());
  setters.setActiveImageIds(new Set());
  setters.setActiveSoundIds(new Set());
  setters.setActiveVideoOverlayIds(new Set());
  setters.setActiveJingleId(null);
  setters.setActiveLayoutId(null);
  setters.setActiveLayoutPreviewId(null);
  setters.setActiveSceneLayoutIds([]);
  setters.setIsStreamButtonActive(false);
  setters.setIsRecording(false);
  setters.setRecordingSessionId(null);
  setters.setRecordingStartedAt(null);
  setters.setPoweredOffSlots(new Set());
  setters.setIsStreaming(false);

  // Le Switch audio doit repartir sur "Source" : son mode "Fixe" désigne un slot du
  // deck qui vient d'être vidé. Laissé tel quel, il continuait à couper le son de
  // toutes les sources au profit d'une source fixe devenue introuvable — y compris
  // après un retour sur la playlist, sans rien dans l'UI pour l'expliquer.
  setters.setAudioSwitchState?.({ mode: 'source', fixedAudioSourceId: '' });

  // ── Contenu des slots : images, sons, PiP, jingles, partages d'écran ET scènes ne
  //    sont plus remis à zéro ici — config persistante par chaîne, voir le
  //    commentaire sur RegieResetSetters plus haut.

  // ── 9. localStorage ──────────────────────────────────────────────────────
  // clearSnapshot() efface le snapshot de session.
  // clearMediartcLocalStorage() efface les slots (images, sons, jingles, etc.)
  // qui sont persistés automatiquement par MediaStreamContext.
  if (options.clearLocalStorage) {
    clearSnapshot();
    clearMediartcLocalStorage();
  }
}